// A stored batch read again against changed fields (`refresh-batch-schema`) or newly confirmed types
// (`apply-confirmed-type-examples`). CapturePoint's main process held the engine on a pipe for as long as
// that took; over Paige's channel a field changed on a type that holds most of a 1,000-document batch
// outlived the 30-minute request and saved nothing. The engine host now answers a run's ticket for both
// (services/Paige.Engine.Host/BatchRereadRuns.cs) and the screen follows it through `refresh-status`,
// which the API never mirrors into the ledger as a new reading; the screen still gets the answer the
// call gave. An engine that answers outright (a formats-only recheck) is passed through as before.
import type { EngineCall } from './desktop-commands';
import { followRun, isTicket } from './process-runs';

/** The question asked after a batch re-read's run. */
export const rereadStatus = 'refresh-status';

/** One re-read command, followed to its answer when the engine answers a ticket. */
export async function rereadBatch<T>(
  call: EngineCall,
  command: 'refresh-batch-schema' | 'apply-confirmed-type-examples',
  args: Record<string, unknown>,
  wait?: (ms: number) => Promise<void>,
): Promise<T> {
  const first = await call<T | unknown>(command, args);
  if (!isTicket(first)) return first as T;
  return followRun<T>(call, first, () => undefined, wait, rereadStatus);
}

/** The screen's two re-read controls, each a run the screen follows (bound in desktop-adapter.ts). */
export function batchRereadRuns(call: EngineCall): Record<string, unknown> {
  return {
    applyConfirmedTypeExamples: (request: {
      jobId: string;
      batchId: string;
      documentTypes: unknown;
      loadedDocumentIds?: unknown;
    }) =>
      rereadBatch(call, 'apply-confirmed-type-examples', {
        jobId: request.jobId,
        batchId: request.batchId,
        documentTypes: request.documentTypes,
        documentIds: request.loadedDocumentIds,
      }),
    refreshBatchSchema: (request: object) =>
      rereadBatch(call, 'refresh-batch-schema', request as Record<string, unknown>),
  };
}
